// src/components/SalesOrder.jsx - Fixed Category Filter
import React, { useState, useEffect, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faCheckCircle,
  faCancel,
  faReceipt,
  faTrash,
  faSpinner,
  faCheck,
  faCashRegister,
  faPlus,
  faBarcode,
  faMinus,
  faSearch,
  faGlobe,
  faStore,
  faMoneyBill,
  faCreditCard,
  faMobileAlt,
  faUniversity,
  faPercent,
  faInfoCircle,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faNewspaper,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";
import { useNavigationEvent } from "navigation-react";
import defaultProductImg from "./images/product.png";

const ls = new SecureLS({ encodingType: "aes" });

// Function to fetch CSRF token - NO Basic Auth
const fetchCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "text/plain",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.status}`);
    }

    const token = await response.text();
    return token.trim();
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
    throw error;
  }
};

// Function to get cached token or fetch new one
const getCsrfToken = async (forceRefresh = false) => {
  try {
    if (!forceRefresh) {
      const cachedToken = ls.get("usercsrf_token");
      if (cachedToken && cachedToken.trim()) {
        return cachedToken.trim();
      }
    }

    const freshToken = await fetchCsrfToken();
    ls.set("usercsrf_token", freshToken);
    return freshToken;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    const cachedToken = ls.get("usercsrf_token");
    if (cachedToken) {
      return cachedToken;
    }
    throw error;
  }
};

// Updated fetcher - NO Authorization header
const fetcher = async (url) => {
  try {
    let csrfToken = ls.get("usercsrf_token");

    if (!csrfToken) {
      csrfToken = await fetchCsrfToken();
      ls.set("usercsrf_token", csrfToken);
    }

    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// Function to deduplicate products based on product_uuid
const deduplicateProducts = (products) => {
  if (!products || !Array.isArray(products)) return [];

  const seen = new Map();

  for (const product of products) {
    const uuid =
      product.product_uuid || product.field_product_image_uuid || product.id;

    if (!seen.has(uuid)) {
      seen.set(uuid, product);
    }
  }

  return Array.from(seen.values());
};

// Payment Options - ORIGINAL (Kept exactly as you had)
const paymentOptions = [
  { value: "cash", label: "Cash", icon: faMoneyBill, color: "#4CAF50" },
  { value: "mobile_money", label: "Mobile Money", icon: faMobileAlt, color: "#9C27B0" },
  { value: "card", label: "Card", icon: faCreditCard, color: "#2196F3" },
  { value: "bank_transfer", label: "Bank Transfer", icon: faUniversity, color: "#FF9800" },
];

const orderTypeOptions = [
  { value: "pos", label: "POS", icon: faStore },
  { value: "online", label: "Online", icon: faGlobe },
];

const discountOptions = [5, 10, 15, 20];
const ITEMS_PER_PAGE = 50;

// Numeric Keyboard Component - Touch Optimized
const NumericKeyboard = ({ onKeyPress, onClear, onFullAmount, value, maxAmount }) => {
  const keys = [
    ["7", "8", "9"],
    ["4", "5", "6"],
    ["1", "2", "3"],
    ["00", "0", "."],
  ];

  const handleKeyClick = (key) => {
    let newValue = value || "";

    if (key === "00") {
      newValue = newValue + "00";
    } else {
      newValue = newValue + key;
    }

    if (key === "." && value && value.includes(".")) {
      return;
    }

    if (newValue.includes(".")) {
      const parts = newValue.split(".");
      if (parts[1] && parts[1].length > 2) {
        return;
      }
    }

    onKeyPress(newValue);
  };

  return (
    <div style={numericKeyboardStyles.container}>
      <div style={numericKeyboardStyles.keypad}>
        {keys.map((row, rowIndex) => (
          <div key={rowIndex} style={numericKeyboardStyles.row}>
            {row.map((key) => (
              <button
                key={key}
                style={numericKeyboardStyles.key}
                onClick={() => handleKeyClick(key)}
                type="button"
              >
                {key}
              </button>
            ))}
          </div>
        ))}
        <div style={numericKeyboardStyles.doubleRow}>
          <button
            style={{
              ...numericKeyboardStyles.clearKey,
              flex: 1,
            }}
            onClick={onClear}
            type="button"
          >
            Clear
          </button>
          <button
            style={{
              ...numericKeyboardStyles.fullAmountKey,
              flex: 1,
            }}
            onClick={onFullAmount}
            type="button"
          >
            Full Amount
          </button>
        </div>
      </div>
    </div>
  );
};

const numericKeyboardStyles = {
  container: {
    padding: "12px",
    backgroundColor: "#F1F5F9",
    borderRadius: "12px",
    border: "1px solid #E2E8F0",
  },
  keypad: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "6px",
  },
  doubleRow: {
    display: "flex",
    gap: "6px",
  },
  key: {
    padding: "16px",
    backgroundColor: "white",
    border: "2px solid #CBD5E1",
    borderRadius: "12px",
    fontSize: "24px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.1s",
    textAlign: "center",
    minHeight: "98px",
    minWidth: "64px",
    height: "64px",
    touchAction: "manipulation",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
  },
  clearKey: {
    backgroundColor: "#dedddd",
    padding: "16px",
    border: "2px solid #dedddd",
    borderRadius: "12px",
    fontSize: "18px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.1s",
    textAlign: "center",
    minWidth: "64px",
    height: "64px",
    touchAction: "manipulation",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    color: "#000000"
  },
  fullAmountKey: {
    backgroundColor: "#dedddd",
    padding: "16px",
    border: "2px solid #dedddd",
    borderRadius: "12px",
    fontSize: "18px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.1s",
    textAlign: "center",
    minWidth: "64px",
    height: "64px",
    touchAction: "manipulation",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    color: "#000000"
  },
};

export default function SalesOrder() {
  const { stateNavigator } = useNavigationEvent();
  const cashierUid = ls.get("currentuserid");

  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderType, setOrderType] = useState("pos");
  const [paymentType, setPaymentType] = useState("cash");
  const [amountPaid, setAmountPaid] = useState("");
  const [discount, setDiscount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [barcode, setBarcode] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [emailSent, setEmailSent] = useState(false);

  // Discount state for modal
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isDiscountApplied, setIsDiscountApplied] = useState(false);

  const [newCustomerForm, setNewCustomerForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });

  const barcodeInputRef = useRef();
  const customerInputRef = useRef();

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const discountCalc = parseFloat(discount) || 0;
  const totalAmount = subtotal - discountCalc;
  const paidAmount = parseFloat(amountPaid) || 0;
  const remainingBalance = totalAmount - paidAmount;

  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_phone_number,field_customer_address,field_customer_email&page[limit]=1000`,
    fetcher,
  );

  // Direct product fetch - API returns flat array
  const { data: productsRaw } = useSWR(
    `${process.env.REACT_APP_PRODUCT_LIST_API}`,
    fetcher,
  );

  // Fetch categories
  const { data: categoriesData } = useSWR(
    `${process.env.REACT_APP_CATEGORY_API}?page[limit]=100`,
    fetcher,
  );

  const categories = useMemo(() => {
    if (!categoriesData?.data) return [];
    return categoriesData.data;
  }, [categoriesData]);

  // Create category mapping for ID normalization
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      const catId = cat.id;
      map[catId] = catId;
      
      if (!isNaN(catId)) {
        map[Number(catId)] = catId;
        map[String(catId)] = catId;
      }
      
      if (cat.attributes) {
        if (cat.attributes.field_category_id) {
          map[cat.attributes.field_category_id] = catId;
        }
        if (cat.attributes.field_category_target_id) {
          map[cat.attributes.field_category_target_id] = catId;
        }
        if (cat.attributes.category_id) {
          map[cat.attributes.category_id] = catId;
        }
        if (cat.attributes.cid) {
          map[cat.attributes.cid] = catId;
        }
        // Also map the name to ID for lookup
        if (cat.attributes.name) {
          map[cat.attributes.name.toLowerCase()] = catId;
        }
      }
    });
    return map;
  }, [categories]);

  // Deduplicate products at the data level
  const products = useMemo(() => {
    const uniqueProducts = deduplicateProducts(productsRaw);
    return uniqueProducts;
  }, [productsRaw]);

  // Build product category map for faster filtering
  const productCategoryMap = useMemo(() => {
    if (!products || !Array.isArray(products)) return {};
    
    const map = {};
    
    products.forEach((p) => {
      // Get the product's category ID from various possible locations
      let catId = null;
      
      // Check direct properties
      if (p.field_category) catId = p.field_category;
      else if (p.category) catId = p.category;
      else if (p.field_category_target_id) catId = p.field_category_target_id;
      else if (p.field_category_id) catId = p.field_category_id;
      else if (p.category_id) catId = p.category_id;
      else if (p.cid) catId = p.cid;
      
      // Check in attributes
      if (!catId && p.attributes) {
        if (p.attributes.field_category) catId = p.attributes.field_category;
        else if (p.attributes.category) catId = p.attributes.category;
        else if (p.attributes.field_category_target_id) catId = p.attributes.field_category_target_id;
        else if (p.attributes.field_category_id) catId = p.attributes.field_category_id;
        else if (p.attributes.category_id) catId = p.attributes.category_id;
        else if (p.attributes.cid) catId = p.attributes.cid;
      }
      
      // Check in relationships
      if (!catId && p.relationships) {
        const relCategory = p.relationships.field_category;
        if (relCategory && relCategory.data) {
          if (Array.isArray(relCategory.data)) {
            catId = relCategory.data[0]?.id;
          } else {
            catId = relCategory.data.id;
          }
        }
      }
      
      // If catId is an object, extract the id
      if (catId && typeof catId === 'object') {
        catId = catId.id || catId.target_id || catId.value || null;
      }
      
      if (catId) {
        const catKey = String(catId);
        if (!map[catKey]) {
          map[catKey] = [];
        }
        map[catKey].push(p);
      }
    });
    
    return map;
  }, [products]);

  const filteredCustomers =
    customers?.data?.filter(
      (c) =>
        c.attributes.title
          .toLowerCase()
          .includes(customerSearchTerm.toLowerCase()) ||
        c.attributes.field_phone_number
          ?.toLowerCase()
          .includes(customerSearchTerm.toLowerCase()),
    ) || [];

  // FIXED: Filter products with proper category handling
  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    
    // If "all" is selected, return all products with search filter
    if (selectedCategory === "all") {
      const searchTerm = productSearchTerm.toLowerCase().trim();
      if (!searchTerm) {
        return products;
      }
      return products.filter((p) => {
        if (!p) return false;
        return (p.title || "").toLowerCase().includes(searchTerm) || 
               (p.field_sku || "").toLowerCase().includes(searchTerm);
      });
    }
    
    // Get products for the selected category
    let categoryProducts = [];
    
    // Try direct lookup first
    categoryProducts = productCategoryMap[selectedCategory] || [];
    
    // If no products found, try with categoryMap
    if (categoryProducts.length === 0) {
      // Try to find the category ID from the map
      const mappedId = categoryMap[selectedCategory] || categoryMap[String(selectedCategory)] || categoryMap[Number(selectedCategory)];
      if (mappedId) {
        categoryProducts = productCategoryMap[mappedId] || productCategoryMap[String(mappedId)] || [];
      }
    }
    
    // If still no products, try case-insensitive name match
    if (categoryProducts.length === 0) {
      const selectedName = selectedCategory.toLowerCase();
      const categoryEntry = Object.entries(categoryMap).find(([key, value]) => {
        // Check if the key is a name
        return key.toLowerCase() === selectedName && typeof value === 'string';
      });
      if (categoryEntry) {
        categoryProducts = productCategoryMap[categoryEntry[1]] || [];
      }
    }
    
    // Apply search filter
    const searchTerm = productSearchTerm.toLowerCase().trim();
    if (!searchTerm) {
      return categoryProducts;
    }
    
    return categoryProducts.filter((p) => {
      if (!p) return false;
      return (p.title || "").toLowerCase().includes(searchTerm) || 
             (p.field_sku || "").toLowerCase().includes(searchTerm);
    });
  }, [products, productSearchTerm, selectedCategory, productCategoryMap, categoryMap]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [productSearchTerm, selectedCategory]);

  useEffect(() => {
    const initCsrf = async () => {
      try {
        const token = await fetchCsrfToken();
        ls.set("usercsrf_token", token);
      } catch (error) {
        console.error("Failed to get initial CSRF token:", error);
        setErrorMessage(
          "Failed to initialize session. Please refresh the page.",
        );
        setModalError(true);
      }
    };
    initCsrf();

    customerInputRef.current?.focus();
    setOrderNumber(`PS-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
  }, []);

  // Debug function - remove after testing
  useEffect(() => {
    if (products && products.length > 0 && categories && categories.length > 0) {
      console.log('Products count:', products.length);
      console.log('Categories count:', categories.length);
      console.log('Product Category Map keys:', Object.keys(productCategoryMap));
      console.log('Category Map:', categoryMap);
      
      // Log first product with its category
      if (products[0]) {
        console.log('Sample product:', {
          title: products[0].title,
          category: products[0].field_category || products[0].category || products[0].attributes?.field_category,
          categoryMapLookup: categoryMap[products[0].field_category || products[0].category || products[0].attributes?.field_category]
        });
      }
    }
  }, [products, categories, productCategoryMap, categoryMap]);

  const isOutOfStock = (product) =>
    Number(product?.field_stock_quantity || 0) <= 0;

  const handleAddProductToCart = (product) => {
    const uuid =
      product.product_uuid || product.field_product_image_uuid || product.id;

    if (!uuid) {
      setErrorMessage(`${product.title} has no UUID and cannot be added`);
      setModalError(true);
      return;
    }

    if (!product.field_sku) {
      setErrorMessage(`${product.title} has no SKU and cannot be added`);
      setModalError(true);
      return;
    }

    setItems((prev) => {
      const existing = prev.find((i) => i.product_uuid === uuid);

      if (existing) {
        return prev.map((i) =>
          i.product_uuid === uuid ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }

      return [
        ...prev,
        {
          product_uuid: uuid,
          title: product.title,
          sku: product.field_sku,
          price: Number(product.field_price),
          quantity: 1,
          imageUrl:
            product.field_product_image && product.field_product_image !== ""
              ? product.field_product_image.startsWith("http")
                ? product.field_product_image
                : `${process.env.REACT_APP_BASE_URL}${product.field_product_image}`
              : defaultProductImg,
        },
      ];
    });
  };

  const updateQuantity = (product_uuid, qty) => {
    const quantity = parseInt(qty, 10);
    if (isNaN(quantity) || quantity < 1) return;

    setItems((prev) =>
      prev.map((i) =>
        i.product_uuid === product_uuid ? { ...i, quantity } : i,
      ),
    );
  };

  const removeItem = (product_uuid) => {
    setItems((prev) => prev.filter((i) => i.product_uuid !== product_uuid));
  };

  const handleBarcodeScan = (code) => {
    const product = products?.find(
      (p) => p.field_gtin === code || p.field_sku === code,
    );

    if (!product) {
      setErrorMessage(`Product "${code}" not found`);
      setModalError(true);
      return;
    }

    if (isOutOfStock(product)) {
      setErrorMessage(`Insufficient stock for ${product.title}`);
      setModalError(true);
      return;
    }

    handleAddProductToCart(product);
    setBarcode("");
    barcodeInputRef.current?.focus();
  };

  const handleAddNewCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const csrfToken = await getCsrfToken(true);

      if (!csrfToken) {
        throw new Error("Could not obtain CSRF token");
      }

      const customerAttributes = {
        title: newCustomerForm.name || "Unknown Customer",
        field_phone_number: newCustomerForm.phone || "",
        field_customer_email: newCustomerForm.email || "",
        field_customer_address: newCustomerForm.address || "",
      };

      const response = await fetch(process.env.REACT_APP_CUSTOMER_API, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          data: {
            type: "node--customer",
            attributes: customerAttributes,
          },
        }),
      });

      const responseText = await response.text();
      let json;
      try {
        json = JSON.parse(responseText);
      } catch (parseError) {
        setErrorMessage(`Server returned: ${responseText.substring(0, 200)}`);
        setModalError(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorDetail =
          json.errors?.[0]?.detail || json.message || `HTTP ${response.status}`;
        setErrorMessage(errorDetail);
        setModalError(true);
        setLoading(false);
        return;
      }

      setCustomer(json.data.id);
      setCustomerName(json.data.attributes.title);
      setCustomerEmail(json.data.attributes.field_customer_email || "");
      setCustomerPhone(json.data.attributes.field_phone_number || "");
      if (json.data.attributes.field_customer_address) {
        setDeliveryAddress(json.data.attributes.field_customer_address);
      }
      setShowCustomerModal(false);
      setNewCustomerForm({ name: "", phone: "", email: "", address: "" });

      mutate(
        `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_phone_number,field_customer_address,field_customer_email&page[limit]=1000`,
      );

      setLoading(false);
    } catch (error) {
      console.error("Add customer error:", error);
      setErrorMessage(error.message || "Network error. Please try again.");
      setModalError(true);
      setLoading(false);
    }
  };

  const handleDiscountSelect = (percent) => {
    if (selectedDiscount === percent && isDiscountApplied) {
      setSelectedDiscount(null);
      setIsDiscountApplied(false);
      setDiscountPercent(0);
      setDiscountAmount(0);
      setDiscount("0");
      return;
    }

    setSelectedDiscount(percent);
    setIsDiscountApplied(true);
    const amount = (subtotal * percent) / 100;
    setDiscountPercent(percent);
    setDiscountAmount(amount);
    setDiscount(amount.toString());
  };

  // Send order confirmation email
  const sendOrderConfirmationEmail = async (orderData, customerEmail, customerName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/email/send-order-confirmation`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": await getCsrfToken(),
          },
          body: JSON.stringify({
            order_uuid: orderData.order_uuid,
            order_number: orderData.order_number,
            customer_email: customerEmail,
            customer_name: customerName,
            items: items.map(item => ({
              title: item.title,
              quantity: item.quantity,
              price: item.price,
              total: item.price * item.quantity,
            })),
            subtotal: subtotal,
            discount: parseFloat(discount) || 0,
            total: totalAmount,
            delivery_address: deliveryAddress,
            payment_method: "Cash on Delivery",
            order_type: "Online",
            notes: notes,
          }),
        }
      );

      if (!response.ok) {
        console.error("Failed to send email notification");
        return false;
      }

      const result = await response.json();
      console.log("Email sent successfully:", result);
      return true;
    } catch (error) {
      console.error("Email sending error:", error);
      return false;
    }
  };

  const processPayment = () => {
    // For online orders, we don't require payment at checkout
    if (orderType === "online") {
      setShowPaymentModal(false);
      handleAddSales();
      return;
    }

    // For POS orders, validate payment
    if (!amountPaid || amountPaid === "" || parseFloat(amountPaid) < 0) {
      setErrorMessage("Please enter a valid payment amount");
      setModalError(true);
      return;
    }

    if (parseFloat(amountPaid) > totalAmount) {
      setErrorMessage("Amount paid cannot exceed total amount");
      setModalError(true);
      return;
    }

    setShowPaymentModal(false);
    handleAddSales();
  };

  const handleAddSales = async () => {
    if (!customer) {
      setErrorMessage("Please select a customer");
      setModalError(true);
      return;
    }

    if (!items.length) {
      setErrorMessage("Please add at least one item to the cart");
      setModalError(true);
      return;
    }

    const invalidItems = items.filter((item) => !item.product_uuid);
    if (invalidItems.length > 0) {
      setErrorMessage(
        `Missing UUID for: ${invalidItems.map((i) => i.title).join(", ")}`,
      );
      setModalError(true);
      return;
    }

    const discountValue = parseFloat(discount) || 0;
    if (discountValue < 0) {
      setErrorMessage("Discount cannot be negative");
      setModalError(true);
      return;
    }

    if (discountValue > subtotal) {
      setErrorMessage("Discount cannot exceed subtotal");
      setModalError(true);
      return;
    }

    // For POS orders, validate payment
    if (orderType !== "online") {
      if (!amountPaid || amountPaid === "" || parseFloat(amountPaid) < 0) {
        setErrorMessage("Please enter a valid payment amount");
        setModalError(true);
        return;
      }

      if (paidAmount > totalAmount) {
        setErrorMessage("Amount paid cannot exceed total amount");
        setModalError(true);
        return;
      }

      if (remainingBalance > 0) {
        const confirmPartial = window.confirm(
          `This is a partial payment. Balance due: GHC${remainingBalance.toFixed(
            2,
          )}\nContinue?`,
        );
        if (!confirmPartial) {
          return;
        }
      }
    }

    setLoading(true);
    try {
      const csrfToken = await getCsrfToken(true);

      if (!csrfToken) {
        throw new Error("Could not obtain CSRF token");
      }

      // For online orders, we use "cash_on_delivery" as payment type
      const paymentTypeValue = orderType === "online" 
        ? "cash_on_delivery" 
        : paymentType;

      const orderData = {
        order_number: orderNumber,
        customer_nid: customer,
        cashier_uid: cashierUid,
        payment_type: paymentTypeValue,
        amount_paid: orderType === "online" ? 0 : paidAmount,
        discount: discountValue,
        payment_reference: orderType === "online" ? `COD_${Date.now()}` : paymentReference,
        notes: notes,
        delivery_address: orderType === "online" ? deliveryAddress : "",
        order_status: orderType === "online" ? "pending_delivery" : "pending",
        is_online_order: orderType === "online",
        items: items.map((i) => ({
          product_uuid: i.product_uuid,
          qty: Number(i.quantity),
          unit_price: Number(i.price),
        })),
      };

      console.log("Order data:", JSON.stringify(orderData, null, 2));

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/checkout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(orderData),
        },
      );

      const responseText = await response.text();
      let json;
      try {
        json = JSON.parse(responseText);
        console.log("Server response:", JSON.stringify(json, null, 2));
      } catch (parseError) {
        setErrorMessage(`Server returned: ${responseText.substring(0, 200)}`);
        setModalError(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        setErrorMessage(json.message || "Failed to create order");
        setModalError(true);
        setLoading(false);
        return;
      }

      let message = "";
      
      // Handle online order response
      if (orderType === "online") {
        message = "✅ Online Order created successfully!";
        
        // Send email confirmation
        if (customerEmail) {
          const emailSent = await sendOrderConfirmationEmail(
            json.data,
            customerEmail,
            customerName
          );
          
          if (emailSent) {
            message += " 📧 A confirmation email has been sent to the customer.";
          } else {
            message += " ⚠️ Email notification failed. Please inform the customer manually.";
          }
        } else {
          message += " ⚠️ No email address provided. Please inform the customer about their order.";
        }
        
        message += " 📦 Payment will be collected upon delivery.";
      } else {
        // POS order response
        message = "Order created successfully! ";
        if (discountValue > 0) {
          message += `(Discount Applied: GHC${discountValue.toFixed(2)}) `;
        }
        if (remainingBalance === 0) {
          message += "✅ Fully Paid";
        } else if (remainingBalance > 0) {
          message += `⚠️ Partial Payment - Balance Due: GHC${remainingBalance.toFixed(
            2,
          )}`;
        }
      }

      const orderTypeLabel = orderType === "online" ? "Online" : "POS";
      message = `[${orderTypeLabel}] ${message}`;

      setSuccessMessage(message);
      setModalSuccessMsg(true);

      setTimeout(() => {
        stateNavigator.navigate("viewsalesinvoicedetails", {
          salesId: json.order_uuid,
        });
      }, 3000);
    } catch (error) {
      console.error("Error creating order:", error);
      setErrorMessage(error.message || "Network error");
      setModalError(true);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setCustomer("");
    setCustomerName("");
    setCustomerEmail("");
    setCustomerPhone("");
    setItems([]);
    setNotes("");
    setBarcode("");
    setCustomerSearchTerm("");
    setProductSearchTerm("");
    setSelectedCategory("all");
    setPaymentType("cash");
    setOrderType("pos");
    setAmountPaid("");
    setDiscount("");
    setPaymentReference("");
    setDeliveryAddress("");
    setSuccessMessage("");
    setModalSuccessMsg(false);
    setSelectedDiscount(null);
    setIsDiscountApplied(false);
    setDiscountPercent(0);
    setDiscountAmount(0);
    setOrderNumber(`PS-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
    setCurrentPage(1);
    setEmailSent(false);
  };

  const getBalanceStyle = () => {
    return remainingBalance > 0 ? styles.balanceDue : styles.balancePaid;
  };

  const getCheckoutBtnStyle = () => {
    const baseStyle = styles.checkoutBtn;
    if (loading || items.length === 0 || !customer) {
      return { ...baseStyle, ...styles.disabled };
    }
    return baseStyle;
  };

  const handleCustomerSelect = (customerId, customerName, customerAddress, customerEmail, customerPhone) => {
    setCustomer(customerId);
    setCustomerName(customerName);
    setCustomerEmail(customerEmail || "");
    setCustomerPhone(customerPhone || "");
    if (customerAddress) {
      setDeliveryAddress(customerAddress);
    }
    setCustomerSearchTerm("");
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div style={styles.paginationContainer}>
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            ...styles.paginationButton,
            ...(currentPage === 1 ? styles.paginationButtonDisabled : {})
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} size="lg" />
        </button>
        
        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              style={styles.paginationButton}
            >
              1
            </button>
            {startPage > 2 && <span style={styles.paginationEllipsis}>...</span>}
          </>
        )}
        
        {pageNumbers.map(page => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              ...styles.paginationButton,
              ...(currentPage === page ? styles.paginationButtonActive : {})
            }}
          >
            {page}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span style={styles.paginationEllipsis}>...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              style={styles.paginationButton}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          style={{
            ...styles.paginationButton,
            ...(currentPage === totalPages ? styles.paginationButtonDisabled : {})
          }}
        >
          <FontAwesomeIcon icon={faChevronRight} size="lg" />
        </button>
      </div>
    );
  };

  const modalSubtotal = subtotal;
  const modalDiscount = parseFloat(discount) || 0;
  const modalTotal = modalSubtotal - modalDiscount;
  const modalPaidAmount = parseFloat(amountPaid) || 0;
  const modalRemainingBalance = modalTotal - modalPaidAmount;

  // Fix: Safely calculate change due
  const changeDue = parseFloat(amountPaid) > modalTotal ? parseFloat(amountPaid) - modalTotal : 0;

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="sales-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Add Sales"
            buttonLabel="Sales Register"
            onClick={() => stateNavigator.navigate("viewsalesregister")}
          />

          <div style={styles.mainContainer}>
            {/* Left Column - Products */}
            <div style={styles.leftColumn}>
              <div style={styles.productsSection}>
                <div style={styles.productSearchHeader}>
                  <div style={styles.searchWrapper}>
                    <div style={styles.searchIconWrapper}>
                      <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} size="lg" />
                    </div>
                    <input
                      type="text"
                      style={styles.productSearchInput}
                      placeholder="Search products..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                      autoFocus
                    />
                    {productSearchTerm && (
                      <button
                        style={styles.clearSearchBtn}
                        onClick={() => setProductSearchTerm("")}
                      >
                        <FontAwesomeIcon icon={faClose} size="lg" />
                      </button>
                    )}
                  </div>
                  
                  <div style={styles.categoryFilterWrapper}>
                    <FontAwesomeIcon icon={faFilter} style={styles.categoryFilterIcon} />
                    <select
                      style={styles.categorySelect}
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">All Categories</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.attributes?.name || category.name || "Unnamed"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={styles.productsGridWrapper}>
                  <div style={styles.productsGrid}>
                    {paginatedProducts.length > 0 ? (
                      paginatedProducts.map((product) => {
                        if (!product) return null;
                        const isOutOfStockItem = isOutOfStock(product);
                        const imageUrl =
                          product.field_product_image &&
                          product.field_product_image !== ""
                            ? product.field_product_image.startsWith("http")
                              ? product.field_product_image
                              : `${process.env.REACT_APP_BASE_URL}${product.field_product_image}`
                            : defaultProductImg;

                        const hasUuid = !!(
                          product.product_uuid ||
                          product.field_product_image_uuid ||
                          product.id
                        );
                        const canAddToCart =
                          !isOutOfStockItem && product.field_sku && hasUuid;

                        return (
                          <div
                            key={
                              product.product_uuid ||
                              product.field_product_image_uuid ||
                              product.id ||
                              Math.random()
                            }
                            style={styles.productCard}
                            onClick={() =>
                              canAddToCart && handleAddProductToCart(product)
                            }
                          >
                            <div style={styles.productImageContainer}>
                              <img
                                src={imageUrl}
                                alt={product.title || "Product"}
                                style={styles.productImage}
                                onError={(e) => {
                                  e.target.src = defaultProductImg;
                                }}
                              />
                              {isOutOfStockItem && (
                                <div style={styles.outOfStockBadge}>
                                  <span style={styles.outOfStockText}>
                                    Out of Stock
                                  </span>
                                </div>
                              )}
                            </div>
                            <div style={styles.productInfo}>
                              <div style={styles.productName}>
                                {product.title || "Unknown Product"}
                              </div>
                              <div style={styles.productSku}>
                                SKU: {product.field_sku || "N/A"}
                              </div>
                              <div style={styles.productFooter}>
                                <div style={styles.productPriceStock}>
                                  <div style={styles.productPrice}>
                                    GHC {Number(product.field_price || 0).toFixed(2)}
                                  </div>
                                  <div style={styles.productStock}>
                                    <span style={styles.stockLabel}>Stock:</span> {product.field_stock_quantity || 0}
                                  </div>
                                </div>
                                {canAddToCart && (
                                  <button
                                    style={styles.addToCartBtn}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddProductToCart(product);
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faPlus}
                                      color="#FF6B35"
                                      size="lg"
                                    />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={styles.noProducts}>
                        {productSearchTerm || selectedCategory !== "all"
                          ? "No products match your filters"
                          : "No products found"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pagination */}
                {renderPagination()}
              </div>
            </div>

            {/* Right Column - Order Form */}
            <div style={styles.rightColumn}>
              <div style={styles.rightColumnInner}>
                {/* Customer and Order Details */}
                <div style={styles.orderDetailsSection}>
                  <div style={styles.section}>
                    <div style={styles.grid2cols}>
                      <div>
                        <label style={styles.label}>Invoice Number</label>
                        <input
                          type="text"
                          style={styles.input}
                          value={orderNumber}
                          readOnly
                        />
                      </div>
                      <div>
                        <label style={styles.label}>Order Type *</label>
                        <div style={styles.orderTypeContainer}>
                          {orderTypeOptions.map((option) => (
                            <button
                              key={option.value}
                              style={{
                                ...styles.orderTypeBtn,
                                ...(orderType === option.value
                                  ? styles.orderTypeBtnActive
                                  : {}),
                              }}
                              onClick={() => setOrderType(option.value)}
                              type="button"
                            >
                              <FontAwesomeIcon
                                icon={option.icon}
                                style={{
                                  marginRight: "8px",
                                  color:
                                    orderType === option.value
                                      ? "#FFF"
                                      : "#666",
                                }}
                                size="lg"
                              />
                              <span
                                style={{
                                  color:
                                    orderType === option.value ? "#FFF" : "#666",
                                  fontSize: "16px",
                                }}
                              >
                                {option.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer and Barcode inline */}
                  <div style={styles.inlineRow}>
                    <div style={styles.inlineCustomer}>
                      <label style={styles.label}>Customer *</label>
                      <div style={styles.rowWithButton}>
                        <div style={styles.searchWrapper}>
                          <input
                            type="text"
                            ref={customerInputRef}
                            style={styles.inputSmall}
                            placeholder="Search customer..."
                            value={customerName || customerSearchTerm}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (customerName && value !== customerName) {
                                setCustomer("");
                                setCustomerName("");
                                setCustomerEmail("");
                                setCustomerPhone("");
                                setDeliveryAddress("");
                              }
                              setCustomerSearchTerm(value);
                            }}
                            required
                          />
                          {!customerName &&
                            customerSearchTerm &&
                            filteredCustomers.length > 0 && (
                              <div style={styles.searchResults}>
                                {filteredCustomers.map((cust) => (
                                  <div
                                    key={cust.id}
                                    style={styles.searchItem}
                                    onClick={() => {
                                      const address = cust.attributes.field_customer_address || "";
                                      const email = cust.attributes.field_customer_email || "";
                                      const phone = cust.attributes.field_phone_number || "";
                                      handleCustomerSelect(
                                        cust.id,
                                        cust.attributes.title,
                                        address,
                                        email,
                                        phone
                                      );
                                    }}
                                  >
                                    <div style={styles.searchItemName}>
                                      {cust.attributes.title}
                                    </div>
                                    <div style={styles.searchItemPhone}>
                                      {cust.attributes.field_phone_number ||
                                        "No phone number"}
                                    </div>
                                    {cust.attributes.field_customer_email && (
                                      <div style={styles.searchItemEmail}>
                                        📧 {cust.attributes.field_customer_email}
                                      </div>
                                    )}
                                    {cust.attributes.field_customer_address && (
                                      <div style={styles.searchItemAddress}>
                                        {cust.attributes.field_customer_address}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                        <button
                          style={styles.iconButtonSmall}
                          onClick={() => setShowCustomerModal(true)}
                          type="button"
                        >
                          <FontAwesomeIcon icon={faPlus} color="#FFF" size="lg" />
                        </button>
                      </div>
                    </div>

                    <div style={styles.inlineBarcode}>
                      <label style={styles.label}>Barcode</label>
                      <div style={styles.rowWithButton}>
                        <input
                          type="text"
                          ref={barcodeInputRef}
                          style={styles.inputSmall}
                          placeholder="Scan barcode..."
                          value={barcode}
                          onChange={(e) => setBarcode(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && barcode.trim()) {
                              handleBarcodeScan(barcode.trim());
                            }
                          }}
                        />
                        <button
                          style={styles.iconButtonSmall}
                          onClick={() => {
                            if (barcode.trim()) {
                              handleBarcodeScan(barcode.trim());
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faBarcode} color="#FFF" size="lg" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {orderType === "online" && (
                    <div style={styles.section}>
                      <label style={styles.label}>Delivery Address *</label>
                      <input
                        type="text"
                        style={styles.input}
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        placeholder="Enter delivery address"
                        required={orderType === "online"}
                      />
                      <div style={styles.addressHint}>
                        <FontAwesomeIcon icon={faGlobe} style={{ marginRight: "6px", fontSize: "14px" }} />
                        {customerName ? (
                          <span>Address for <strong>{customerName}</strong> - Edit if needed</span>
                        ) : (
                          <span>Select a customer first to auto-fill address</span>
                        )}
                      </div>
                      {customerEmail && (
                        <div style={styles.emailHint}>
                          <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "6px", fontSize: "14px" }} />
                          <span>Confirmation email will be sent to: <strong>{customerEmail}</strong></span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Cart Section - Scrollable */}
                <div style={styles.cartSection}>
                  <div style={styles.cartHeader}>
                    Your Order ({items.length} items)
                  </div>
                  <div style={styles.cartItemsList}>
                    {items.length === 0 ? (
                      <div style={styles.emptyCart}>
                        <div style={styles.emptyText}>No items added</div>
                      </div>
                    ) : (
                      items.map((item) => (
                        <div key={item.product_uuid} style={styles.cartItem}>
                          <div style={styles.cartItemInfo}>
                            <div style={styles.cartItemDetails}>
                              <div style={styles.cartItemName}>{item.title}</div>
                              <div style={styles.cartItemSku}>
                                SKU: {item.sku}
                              </div>
                              <div style={styles.cartItemPrice}>
                                GHC {item.price.toFixed(2)}
                              </div>
                            </div>
                          </div>

                          <div style={styles.cartActions}>
                            <button
                              style={styles.qtyBtn}
                              onClick={() =>
                                updateQuantity(
                                  item.product_uuid,
                                  item.quantity - 1,
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faMinus} size="lg" />
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) =>
                                updateQuantity(item.product_uuid, e.target.value)
                              }
                              style={styles.qtyInput}
                            />
                            <button
                              style={styles.qtyBtn}
                              onClick={() =>
                                updateQuantity(
                                  item.product_uuid,
                                  item.quantity + 1,
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={faPlus}
                                color="#1f1717"
                                size="lg"
                              />
                            </button>
                            <button
                              style={styles.deleteBtn}
                              onClick={() => removeItem(item.product_uuid)}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                color="#FF4444"
                                size="lg"
                              />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Summary and Payment Button */}
                <div style={styles.summarySection}>
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryRow}>
                      <span style={styles.summaryLabel}>Subtotal:</span>
                      <span style={styles.summaryValue}>GHC {subtotal.toFixed(2)}</span>
                    </div>
                    {parseFloat(discount) > 0 && (
                      <div style={styles.summaryRow}>
                        <span style={{ color: "#ff9800", ...styles.summaryLabel }}>Discount:</span>
                        <span style={{ color: "#ff9800", fontWeight: "bold", ...styles.summaryValue }}>
                          - GHC {parseFloat(discount).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div style={{ ...styles.summaryRow, ...styles.totalRow }}>
                      <span style={styles.totalLabel}>Total:</span>
                      <span style={styles.totalValue}>
                        GHC {totalAmount.toFixed(2)}
                      </span>
                    </div>
                    {orderType !== "online" && (
                      <div style={{ ...styles.summaryRow, ...styles.balanceRow }}>
                        <span style={styles.balanceLabel}>Balance:</span>
                        <span style={getBalanceStyle()}>
                          GHC {remainingBalance.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {orderType === "online" && (
                      <div style={{ ...styles.summaryRow, ...styles.balanceRow }}>
                        <span style={styles.balanceLabel}>Payment:</span>
                        <span style={{ color: "#FF6B35", fontWeight: "900", fontSize: "18px" }}>
                          Cash on Delivery
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    style={getCheckoutBtnStyle()}
                    onClick={() => {
                      if (orderType === "online") {
                        // For online orders, skip payment modal
                        handleAddSales();
                      } else {
                        setShowPaymentModal(true);
                      }
                    }}
                    disabled={loading || items.length === 0 || !customer}
                  >
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin={true} color="#FFF" size="lg" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheck} color="#FFF" size="lg" />
                        <span style={styles.checkoutText}>
                          {orderType === "online" ? "Place Online Order" : "Proceed to Payment"}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Modal */}
      {showCustomerModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content">
            <div className="modal-body">
              <div className="modal-header">
                <p>Add New Customer</p>
                <button
                  className="close-modal"
                  onClick={() => setShowCustomerModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} size="lg" />
                </button>
              </div>
              <form onSubmit={handleAddNewCustomer} className="edit-patient-form">
                <div style={styles.formGroupWrapper}>
                  <div className="form-group">
                    <label htmlFor="customer-name">Full Name *</label>
                    <input
                      type="text"
                      placeholder="Enter Customer Full Name"
                      value={newCustomerForm.name}
                      onChange={(e) =>
                        setNewCustomerForm({
                          ...newCustomerForm,
                          name: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="customer-contact">Contact Number</label>
                    <input
                      type="text"
                      placeholder="Enter Customer Contact Number"
                      value={newCustomerForm.phone}
                      onChange={(e) =>
                        setNewCustomerForm({
                          ...newCustomerForm,
                          phone: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="customer-emailaddress">Email Address</label>
                  <input
                    type="email"
                    placeholder="Enter Customer Email Address"
                    value={newCustomerForm.email}
                    onChange={(e) =>
                      setNewCustomerForm({
                        ...newCustomerForm,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="customer-address">Location Address</label>
                  <input
                    type="text"
                    placeholder="Enter Customer Address"
                    value={newCustomerForm.address}
                    onChange={(e) =>
                      setNewCustomerForm({
                        ...newCustomerForm,
                        address: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={loading}
                    style={styles.modalSaveBtn}
                  >
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin={true} />
                    ) : (
                      "Add Customer"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal - Only for POS orders */}
      {showPaymentModal && orderType !== "online" && (
        <div className="modal" style={paymentModalStyles.modalOverlay}>
          <div className="overlay" onClick={() => setShowPaymentModal(false)}></div>
          <div className="modal-content" style={paymentModalStyles.modalContent}>
            <div style={paymentModalStyles.modalHeader}>
              <p style={paymentModalStyles.modalTitle}>Payment Details - Order #{orderNumber}</p>
              <button
                style={paymentModalStyles.closeButton}
                onClick={() => setShowPaymentModal(false)}
              >
                <FontAwesomeIcon icon={faClose} size="sm" />
              </button>
            </div>

            <div style={paymentModalStyles.twoColumnLayout}>
              {/* Left Column - Payment Methods & Discounts */}
              <div style={paymentModalStyles.leftColumn}>
                <div style={paymentModalStyles.paymentMethodsSection}>
                  <label style={paymentModalStyles.label}>Payment Method *</label>
                  <div style={paymentModalStyles.paymentMethodsGrid}>
                    {paymentOptions.map((method) => (
                      <button
                        key={method.value}
                        type="button"
                        style={{
                          ...paymentModalStyles.paymentMethodButton,
                          ...(paymentType === method.value
                            ? {
                                borderColor: method.color,
                                backgroundColor: `${method.color}20`,
                                transform: "scale(1.02)",
                              }
                            : {}),
                        }}
                        onClick={() => {
                          setPaymentType(method.value);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={method.icon}
                          style={{ color: method.color, fontSize: "24px" }}
                        />
                        <span style={paymentModalStyles.paymentMethodLabel}>
                          {method.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div style={paymentModalStyles.discountSection}>
                  <div style={paymentModalStyles.discountHeader}>
                    <FontAwesomeIcon icon={faPercent} style={{ color: "#FF6B35", fontSize: "20px" }} />
                    <span style={paymentModalStyles.discountTitle}>Apply Discount</span>
                    {isDiscountApplied && (
                      <span style={paymentModalStyles.discountStatus}>{discountPercent}% Applied</span>
                    )}
                  </div>
                  <div style={paymentModalStyles.discountButtons}>
                    {discountOptions.map((percent) => (
                      <button
                        key={percent}
                        style={{
                          ...paymentModalStyles.discountButton,
                          backgroundColor:
                            selectedDiscount === percent && isDiscountApplied
                              ? "#FF6B35"
                              : "#F3F4F6",
                          color:
                            selectedDiscount === percent && isDiscountApplied
                              ? "white"
                              : "#1F2937",
                          border:
                            selectedDiscount === percent && isDiscountApplied
                              ? "2px solid #FF6B35"
                              : "2px solid transparent",
                          transform:
                            selectedDiscount === percent && isDiscountApplied
                              ? "scale(1.05)"
                              : "scale(1)",
                        }}
                        onClick={() => handleDiscountSelect(percent)}
                      >
                        {percent}%
                        {selectedDiscount === percent && isDiscountApplied && (
                          <FontAwesomeIcon icon={faCheckCircle} style={{ marginLeft: "8px", fontSize: "16px" }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={paymentModalStyles.notesSection}>
                  <div style={paymentModalStyles.notesHeader}>
                    <FontAwesomeIcon icon={faNewspaper} style={{ color: "#3B82F6", fontSize: "20px" }} />
                    <span style={paymentModalStyles.notesTitle}>Order Notes</span>
                  </div>
                  <textarea
                    style={paymentModalStyles.notesTextarea}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes for this order..."
                    rows="3"
                  />
                </div>

                <div style={paymentModalStyles.totalSection}>
                  <div style={paymentModalStyles.totalRow}>
                    <span style={paymentModalStyles.totalLabel}>Total Amount:</span>
                    <span style={paymentModalStyles.totalAmount}>GHS {modalTotal.toFixed(2)}</span>
                  </div>
                  <div style={paymentModalStyles.balanceRow}>
                    <span style={paymentModalStyles.balanceLabel}>Balance Due:</span>
                    <span style={{
                      ...paymentModalStyles.balanceAmount,
                      color: modalRemainingBalance > 0 ? "#000000" : "#000000"
                    }}>
                      GHS {modalRemainingBalance.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div style={paymentModalStyles.actionButtons}>
                  <button
                    type="button"
                    style={paymentModalStyles.completePayButton}
                    onClick={processPayment}
                    disabled={
                      loading ||
                      !amountPaid ||
                      parseFloat(amountPaid) < 0 ||
                      parseFloat(amountPaid) > modalTotal
                    }
                  >
                    {loading ? <FontAwesomeIcon icon={faSpinner} spin size="lg" /> : "Complete Payment"}
                  </button>
                </div>
              </div>

              {/* Right Column - Amount Input with Keypad */}
              <div style={paymentModalStyles.rightColumn}>
                {paymentType === "cash" && (
                  <div style={paymentModalStyles.cashSection}>
                    <label style={paymentModalStyles.label}>Payment Summary</label>

                    <div style={paymentModalStyles.paymentSummaryRow}>
                      <div style={paymentModalStyles.amountCard}>
                        <div style={paymentModalStyles.amountCardLabel}>Amount Paid (GHS)</div>
                        <div style={paymentModalStyles.amountCardValue}>
                          GHS {amountPaid || "0.00"}
                        </div>
                      </div>
                      <div
                        style={{
                          ...paymentModalStyles.amountCard,
                          backgroundColor:
                            parseFloat(amountPaid) > modalTotal ? "#E8F5E9" : "#F8FAFC",
                          border:
                            parseFloat(amountPaid) > modalTotal
                              ? "2px solid #4CAF50"
                              : "2px solid #E2E8F0",
                        }}
                      >
                        <div style={paymentModalStyles.amountCardLabel}>Change Due</div>
                        <div
                          style={{
                            ...paymentModalStyles.amountCardValue,
                            color: parseFloat(amountPaid) > modalTotal ? "#2E7D32" : "#64748B",
                          }}
                        >
                          GHS {changeDue.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    <NumericKeyboard
                      onKeyPress={(value) => setAmountPaid(value)}
                      onClear={() => setAmountPaid("")}
                      onFullAmount={() => setAmountPaid(modalTotal.toString())}
                      value={amountPaid}
                      maxAmount={modalTotal}
                    />

                    {paymentType !== "cash" && (
                      <div style={paymentModalStyles.referenceSection}>
                        <label style={paymentModalStyles.label}>Transaction Reference *</label>
                        <input
                          type="text"
                          style={paymentModalStyles.referenceInput}
                          value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                          placeholder="e.g., MOMO123456"
                        />
                      </div>
                    )}
                  </div>
                )}
                {paymentType !== "cash" && (
                  <div style={paymentModalStyles.nonCashMessage}>
                    <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: "8px", fontSize: "20px" }} />
                    Click "Complete Payment" to process payment via {paymentType.replace("_", " ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color={orderType === "online" ? "blue" : (remainingBalance === 0 ? "green" : "orange")}
          message={successMessage}
          onClose={() => {
            setModalSuccessMsg(false);
            resetForm();
          }}
          rightIcon={faClose}
        />
      )}

      {modalError && (
        <MessageModal
          leftIcon={faCancel}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// All styles remain the same as in your original code
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "rgb(236, 242, 250)",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "20px 20px 4rem 20px",
  },
  mainContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "12px",
    marginTop: "20px",
    alignItems: "stretch",
    maxHeight: "calc(100vh - 200px)",
    minHeight: "700px",
  },
  leftColumn: {
    width: "55%",
    flex: "none",
    backgroundColor: "#FFF",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    border: "1px solid #E0E0E0",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  rightColumn: {
    width: "45%",
    flex: "none",
    backgroundColor: "#FFF",
    borderRadius: "16px",
    padding: "16px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    border: "1px solid #E0E0E0",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  rightColumnInner: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%",
    overflow: "hidden",
  },
  section: {
    marginBottom: "12px",
  },
  label: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    width: "100%",
    height: "48px",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    fontSize: "16px",
    backgroundColor: "#F5F5F5",
    outline: "none",
    transition: "border 0.3s",
    touchAction: "manipulation",
  },
  inputSmall: {
    width: "100%",
    height: "48px",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    fontSize: "15px",
    backgroundColor: "#F5F5F5",
    outline: "none",
    transition: "border 0.3s",
    touchAction: "manipulation",
  },
  grid2cols: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px",
  },
  inlineRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "12px",
  },
  inlineCustomer: {
    display: "flex",
    flexDirection: "column",
  },
  inlineBarcode: {
    display: "flex",
    flexDirection: "column",
  },
  rowWithButton: {
    display: "flex",
    gap: "8px",
    position: "relative",
  },
  searchWrapper: {
    flex: 1,
    position: "relative",
  },
  iconButton: {
    width: "48px",
    height: "48px",
    backgroundColor: "#000000",
    borderRadius: "10px",
    border: "1px solid #000000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s",
    flexShrink: 0,
    touchAction: "manipulation",
  },
  iconButtonSmall: {
    width: "44px",
    height: "48px",
    backgroundColor: "#000000",
    borderRadius: "10px",
    border: "1px solid #000000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s",
    flexShrink: 0,
    touchAction: "manipulation",
  },
  searchResults: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#FFF",
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    maxHeight: "200px",
    overflowY: "auto",
    zIndex: 1000,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    marginTop: "4px",
  },
  searchItem: {
    padding: "12px",
    borderBottom: "1px solid #F0F0F0",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  searchItemName: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "2px",
  },
  searchItemPhone: {
    fontSize: "15px",
    color: "#666",
    letterSpacing: "1px",
  },
  searchItemEmail: {
    fontSize: "13px",
    color: "#1976d2",
    marginTop: "2px",
  },
  searchItemAddress: {
    fontSize: "13px",
    color: "#999",
    marginTop: "2px",
    fontStyle: "italic",
  },
  orderDetailsSection: {
    backgroundColor: "#FFF",
    borderRadius: "12px",
    padding: "14px",
    border: "1px solid #E0E0E0",
    flexShrink: 0,
    marginBottom: "8px",
  },
  productsSection: {
    backgroundColor: "#FFF",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
  },
  productSearchHeader: {
    display: "flex",
    gap: "10px",
    marginBottom: "12px",
    flexShrink: 0,
    alignItems: "center",
  },
  searchIconWrapper: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
  },
  searchIcon: {
    color: "#3D3D3D",
    fontSize: "18px",
  },
  productSearchInput: {
    width: "100%",
    padding: "12px 14px 12px 48px",
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    fontSize: "15px",
    backgroundColor: "#F5F5F5",
    outline: "none",
    transition: "all 0.3s",
    height: "44px",
    touchAction: "manipulation",
  },
  clearSearchBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#999",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    touchAction: "manipulation",
  },
  categoryFilterWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#F5F5F5",
    borderRadius: "10px",
    padding: "0 12px",
    border: "1px solid #E0E0E0",
    flexShrink: 0,
    minWidth: "140px",
  },
  categoryFilterIcon: {
    color: "#666",
    fontSize: "14px",
  },
  categorySelect: {
    padding: "10px 8px",
    border: "none",
    backgroundColor: "transparent",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    cursor: "pointer",
    touchAction: "manipulation",
    minHeight: "44px",
  },
  productsGridWrapper: {
    flex: 1,
    overflow: "hidden",
  },
  productsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    height: "100%",
    overflowY: "auto",
    padding: "2px",
    alignContent: "start",
    WebkitOverflowScrolling: "touch",
  },
  productCard: {
    borderRadius: "10px",
    overflow: "hidden",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    backgroundColor: "#fff",
    border: "1px solid #f0f0f0",
    display: "flex",
    flexDirection: "column",
    minHeight: "310px",
    touchAction: "manipulation",
  },
  productImageContainer: {
    width: "100%",
    height: "180px",
    position: "relative",
    backgroundColor: "#f8f9fa",
    flexShrink: 0,
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  outOfStockBadge: {
    position: "absolute",
    top: "6px",
    right: "6px",
    backgroundColor: "rgba(220, 53, 69, 0.9)",
    padding: "2px 10px",
    borderRadius: "4px",
    zIndex: 2,
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  },
  outOfStockText: {
    color: "#FFF",
    fontWeight: "bold",
    fontSize: "11px",
  },
  productInfo: {
    padding: "8px 10px",
    backgroundColor: "#FFF",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  productName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a1a1a",
    marginBottom: "2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  productSku: {
    fontSize: "16px",
    color: "#999",
    marginBottom: "4px",
  },
  productFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "4px",
  },
  productPriceStock: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flex: 1,
    minWidth: 0,
  },
  productPrice: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#FF6B35",
  },
  productStock: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#666",
    marginTop: "2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  stockLabel: {
    fontWeight: "600",
    color: "#444",
  },
  addToCartBtn: {
    background: "#f8f9fa",
    border: "1px solid #e0e0e0",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
    flexShrink: 0,
    touchAction: "manipulation",
  },
  noProducts: {
    textAlign: "center",
    padding: "40px",
    color: "#999",
    fontSize: "15px",
    gridColumn: "1 / -1",
  },
  orderTypeContainer: {
    display: "flex",
    gap: "8px",
    marginTop: "4px",
  },
  orderTypeBtn: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: "8px",
    border: "2px solid #E0E0E0",
    backgroundColor: "#FFF",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s",
    fontSize: "14px",
    fontWeight: "500",
    touchAction: "manipulation",
    minHeight: "44px",
  },
  orderTypeBtnActive: {
    backgroundColor: "#007bff",
    borderColor: "#007bff",
    color: "#FFF",
  },
  addressHint: {
    fontSize: "13px",
    color: "#666",
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
  },
  emailHint: {
    fontSize: "13px",
    color: "#1976d2",
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
    backgroundColor: "#e3f2fd",
    padding: "6px 12px",
    borderRadius: "6px",
  },
  cartSection: {
    backgroundColor: "#FFF",
    borderRadius: "12px",
    padding: "14px",
    border: "1px solid #E0E0E0",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: "100px",
  },
  cartHeader: {
    fontSize: "16px",
    fontWeight: "bold",
    marginBottom: "8px",
    color: "#333",
    paddingBottom: "8px",
    borderBottom: "2px solid #f0f0f0",
    flexShrink: 0,
  },
  cartItemsList: {
    flex: 1,
    overflowY: "auto",
    paddingRight: "4px",
    WebkitOverflowScrolling: "touch",
  },
  emptyCart: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: "20px",
  },
  emptyText: {
    marginTop: "8px",
    color: "#999",
    fontSize: "16px",
  },
  cartItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #F0F0F0",
  },
  cartItemInfo: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  cartItemDetails: {
    flex: 1,
    minWidth: 0,
  },
  cartItemName: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "1px",
  },
  cartItemSku: {
    fontSize: "14px",
    color: "#999",
    marginBottom: "1px",
  },
  cartItemPrice: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#FF6B35",
  },
  cartActions: {
    display: "flex",
    alignItems: "center",
    gap: "30px",
    flexShrink: 0,
  },
  qtyBtn: {
    width: "46px",
    height: "46px",
    backgroundColor: "#F0F0F0",
    borderRadius: "6px",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
    touchAction: "manipulation",
  },
  qtyInput: {
    width: "46px",
    height: "46px",
    textAlign: "center",
    padding: "2px",
    borderRadius: "4px",
    border: "1px solid #E0E0E0",
    fontSize: "14px",
    fontWeight: "bold",
    touchAction: "manipulation",
  },
  deleteBtn: {
    width: "46px",
    height: "46px",
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    touchAction: "manipulation",
  },
  summarySection: {
    backgroundColor: "#FFF",
    borderRadius: "12px",
    padding: "12px",
    border: "1px solid #E0E0E0",
    flexShrink: 0,
    marginTop: "8px",
  },
  summaryCard: {
    backgroundColor: "#F9F9F9",
    borderRadius: "10px",
    padding: "12px",
    marginBottom: "8px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "6px",
    fontSize: "14px",
  },
  summaryLabel: {
    fontWeight: "500",
    color: "#555",
  },
  summaryValue: {
    fontWeight: "600",
    color: "#333",
  },
  totalRow: {
    borderTop: "1px solid #E0E0E0",
    paddingTop: "8px",
    marginTop: "6px",
    marginBottom: "6px",
  },
  totalLabel: {
    fontWeight: "bold",
    fontSize: "16px",
  },
  totalValue: {
    fontWeight: "900",
    fontSize: "18px",
    color: "#1976d2",
  },
  balanceRow: {
    borderTop: "1px solid #E0E0E0",
    paddingTop: "8px",
    marginTop: "6px",
  },
  balanceLabel: {
    fontWeight: "bold",
    fontSize: "15px",
  },
  balanceDue: {
    color: "#FF4444",
    fontWeight: "900",
    fontSize: "18px",
  },
  balancePaid: {
    color: "#4CAF50",
    fontWeight: "900",
    fontSize: "18px",
  },
  checkoutBtn: {
    width: "100%",
    backgroundColor: "#148918",
    borderRadius: "10px",
    padding: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s",
    marginTop: "6px",
    minHeight: "54px",
    touchAction: "manipulation",
  },
  checkoutText: {
    color: "#FFF",
    fontSize: "16px",
    fontWeight: "600",
  },
  disabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  formGroupWrapper: {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",
  },
  modalSaveBtn: {
    backgroundColor: "#3D3D3D",
    color: "#FFF",
    padding: "14px 24px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    minHeight: "48px",
    touchAction: "manipulation",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginTop: "8px",
    padding: "6px 0",
    borderTop: "1px solid #E0E0E0",
    flexShrink: 0,
  },
  paginationButton: {
    padding: "6px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    transition: "all 0.2s",
    minWidth: "60px",
    minHeight: "54px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    touchAction: "manipulation",
  },
  paginationButtonActive: {
    backgroundColor: "#000000",
    color: "white",
    borderColor: "#000000",
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  paginationEllipsis: {
    padding: "0 4px",
    color: "#999",
    fontSize: "14px",
  },
};

// Payment Modal Styles - Touch Optimized
const paymentModalStyles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    position: "relative",
    backgroundColor: "#fff",
    borderRadius: "20px",
    width: "90%",
    maxWidth: "1200px",
    height: "84%",
    maxHeight: "900px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 24px",
    borderBottom: "2px solid #E2E8F0",
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "700",
    margin: 0,
    color: "#1E293B",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "28px",
    cursor: "pointer",
    padding: "8px",
    borderRadius: "12px",
    color: "#64748B",
    minWidth: "48px",
    minHeight: "48px",
    touchAction: "manipulation",
  },
  twoColumnLayout: {
    display: "flex",
    gap: "16px",
    padding: "16px",
    flex: 1,
    overflow: "hidden",
  },
  leftColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    overflowY: "auto",
    paddingRight: "8px",
  },
  rightColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    overflowY: "auto",
    paddingLeft: "8px",
  },
  paymentMethodsSection: {
    flexShrink: 0,
  },
  label: {
    display: "block",
    marginBottom: "10px",
    fontWeight: "600",
    fontSize: "16px",
    color: "#1E293B",
  },
  paymentMethodsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
  },
  paymentMethodButton: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px",
    border: "2px solid #E2E8F0",
    borderRadius: "12px",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    minHeight: "64px",
    height: "64px",
    touchAction: "manipulation",
  },
  paymentMethodLabel: {
    fontSize: "16px",
    fontWeight: "600",
  },
  discountSection: {
    backgroundColor: "#F8FAFC",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #E2E8F0",
    flexShrink: 0,
  },
  discountHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: "1px solid #E2E8F0",
  },
  discountTitle: {
    fontWeight: "700",
    fontSize: "15px",
    color: "#1E293B",
  },
  discountStatus: {
    fontSize: "12px",
    color: "#10B981",
    fontWeight: "600",
    backgroundColor: "#D1FAE5",
    padding: "4px 10px",
    borderRadius: "12px",
  },
  discountButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  discountButton: {
    padding: "10px 18px",
    border: "2px solid transparent",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    minHeight: "52px",
    height: "52px",
    minWidth: "58px",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    touchAction: "manipulation",
  },
  notesSection: {
    backgroundColor: "#F8FAFC",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #E2E8F0",
    flexShrink: 0,
  },
  notesHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: "1px solid #E2E8F0",
  },
  notesTitle: {
    fontWeight: "700",
    fontSize: "15px",
    color: "#1E293B",
  },
  notesTextarea: {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
    fontSize: "15px",
    backgroundColor: "#FFFFFF",
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    minHeight: "60px",
    touchAction: "manipulation",
  },
  totalSection: {
    backgroundColor: "#F2F2F2",
    padding: "16px",
    borderRadius: "12px",
    flexShrink: 0,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  totalLabel: {
    fontSize: "17px",
    color: "#64748B",
    fontWeight: "600",
  },
  totalAmount: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#FF6B35",
  },
  balanceRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "8px",
    borderTop: "1px solid #E2E8F0",
  },
  balanceLabel: {
    fontSize: "15px",
    color: "#64748B",
    fontWeight: "500",
  },
  balanceAmount: {
    fontSize: "22px",
    fontWeight: "700",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "4px",
    flexShrink: 0,
  },
  completePayButton: {
    flex: 1,
    padding: "16px",
    backgroundColor: "#4CAF50",
    border: "none",
    borderRadius: "10px",
    fontSize: "18px",
    fontWeight: "700",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "64px",
    height: "64px",
    transition: "all 0.2s ease",
    touchAction: "manipulation",
  },
  cashSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minHeight: 0,
  },
  paymentSummaryRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
  },
  amountCard: {
    flex: 1,
    backgroundColor: "#F8FAFC",
    border: "2px solid #E2E8F0",
    borderRadius: "10px",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "80px",
  },
  amountCardLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#64748B",
    marginBottom: "6px",
  },
  amountCardValue: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#1E293B",
  },
  referenceSection: {
    marginTop: "8px",
  },
  referenceInput: {
    width: "100%",
    padding: "14px 16px",
    border: "2px solid #E2E8F0",
    borderRadius: "8px",
    fontSize: "16px",
    outline: "none",
    backgroundColor: "#FFFFFF",
    minHeight: "52px",
    touchAction: "manipulation",
  },
  nonCashMessage: {
    padding: "16px",
    backgroundColor: "#E0F2FE",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#0284C7",
    textAlign: "center",
    border: "1px solid #BAE6FD",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
};

// Responsive Styles - Touch Optimized
const responsiveStyles = document.createElement("style");
responsiveStyles.textContent = `
  @media (max-width: 1200px) {
    .sales-container .mainContainer {
      flex-direction: column !important;
      max-height: none !important;
    }
    .sales-container .leftColumn {
      width: 100% !important;
      min-height: 400px !important;
      max-height: 500px !important;
    }
    .sales-container .rightColumn {
      width: 100% !important;
      min-height: 400px !important;
      max-height: 500px !important;
    }
    .sales-container .productsGrid {
      grid-template-columns: repeat(3, 1fr) !important;
      gap: 16px !important;
    }
    .modal-content {
      width: 95% !important;
      height: 90vh !important;
      max-height: 90vh !important;
    }
    .twoColumnLayout {
      flex-direction: column !important;
    }
    .leftColumn, .rightColumn {
      flex: none !important;
      width: 100% !important;
      padding: 0 !important;
    }
    .paymentMethodsGrid {
      grid-template-columns: repeat(3, 1fr) !important;
    }
    .numericKeyboard .key {
      min-height: 56px !important;
      height: 56px !important;
      font-size: 20px !important;
      padding: 12px !important;
    }
    .sales-container .categoryFilterWrapper {
      min-width: 120px !important;
    }
  }

  @media (max-width: 992px) {
    .sales-container .productsGrid {
      grid-template-columns: repeat(3, 1fr) !important;
      gap: 16px !important;
    }
    .sales-container .inlineRow {
      grid-template-columns: 1fr !important;
    }
    .sales-container .productSearchHeader {
      flex-wrap: wrap !important;
    }
    .sales-container .categoryFilterWrapper {
      width: 100% !important;
    }
  }

  @media (max-width: 768px) {
    .sales-container .grid2cols {
      grid-template-columns: 1fr !important;
    }
    .sales-container .productsGrid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 12px !important;
    }
    .sales-container .productImageContainer {
      height: 100px !important;
    }
    .sales-container .productName {
      font-size: 12px !important;
    }
    .sales-container .productPrice {
      font-size: 14px !important;
    }
    .sales-container .productInfo {
      padding: 6px 8px !important;
    }
    .sales-container .cartImage {
      width: 35px !important;
      height: 35px !important;
    }
    .sales-container .cartItemName {
      font-size: 12px !important;
    }
    .sales-container .cartItemPrice {
      font-size: 12px !important;
    }
    .sales-container .qtyBtn {
      width: 28px !important;
      height: 28px !important;
    }
    .sales-container .qtyInput {
      width: 32px !important;
      height: 28px !important;
      font-size: 13px !important;
    }
    .sales-container .deleteBtn {
      width: 28px !important;
      height: 28px !important;
    }
    .sales-container .container {
      padding: 16px !important;
    }
    .sales-container .leftColumn {
      max-height: 450px !important;
    }
    .sales-container .rightColumn {
      max-height: 450px !important;
    }
    .modal-content {
      width: 98% !important;
      height: 95vh !important;
      max-height: 95vh !important;
    }
    .paymentMethodsGrid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
    .paymentSummaryRow {
      flex-direction: column !important;
    }
    .amountCard {
      min-height: 60px !important;
    }
    .amountCardValue {
      font-size: 22px !important;
    }
    .totalAmount {
      font-size: 24px !important;
    }
    .numericKeyboard .key {
      min-height: 48px !important;
      height: 48px !important;
      font-size: 18px !important;
      padding: 10px !important;
    }
    .sales-container .categorySelect {
      font-size: 13px !important;
    }
  }

  @media (max-width: 480px) {
    .sales-container .productsGrid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 8px !important;
    }
    .sales-container .productImageContainer {
      height: 80px !important;
    }
    .sales-container .productName {
      font-size: 11px !important;
    }
    .sales-container .productPrice {
      font-size: 13px !important;
    }
    .sales-container .productSku {
      font-size: 9px !important;
    }
    .sales-container .productStock {
      font-size: 9px !important;
    }
    .sales-container .productInfo {
      padding: 4px 6px !important;
    }
    .sales-container .addToCartBtn {
      width: 26px !important;
      height: 26px !important;
    }
    .sales-container .addToCartBtn svg {
      font-size: 12px !important;
    }
    .sales-container .orderTypeBtn {
      font-size: 12px !important;
      padding: 6px 10px !important;
      min-height: 36px !important;
    }
    .sales-container .input {
      height: 40px !important;
      font-size: 14px !important;
      padding: 8px 10px !important;
    }
    .sales-container .iconButton {
      width: 38px !important;
      height: 38px !important;
    }
    .sales-container .leftColumn {
      max-height: 400px !important;
      min-height: 300px !important;
    }
    .sales-container .rightColumn {
      max-height: 400px !important;
      min-height: 300px !important;
    }
    .paymentMethodsGrid {
      grid-template-columns: 1fr !important;
    }
    .paymentMethodButton {
      min-height: 48px !important;
      height: 48px !important;
      padding: 10px !important;
      font-size: 15px !important;
    }
    .discountButtons {
      gap: 6px !important;
    }
    .discountButton {
      min-height: 40px !important;
      height: 40px !important;
      font-size: 14px !important;
      padding: 4px 12px !important;
    }
    .modalTitle {
      font-size: 18px !important;
    }
    .totalAmount {
      font-size: 22px !important;
    }
    .balanceAmount {
      font-size: 18px !important;
    }
    .completePayButton {
      min-height: 48px !important;
      height: 48px !important;
      font-size: 15px !important;
    }
    .numericKeyboard .key {
      min-height: 40px !important;
      height: 40px !important;
      font-size: 15px !important;
      padding: 6px !important;
      minWidth: 40px !important;
    }
    .numericKeyboard .clearKey, .numericKeyboard .fullAmountKey {
      font-size: 13px !important;
    }
    .sales-container .categorySelect {
      font-size: 12px !important;
      padding: 6px 4px !important;
    }
    .sales-container .categoryFilterWrapper {
      min-width: 100px !important;
      padding: 0 8px !important;
    }
    .sales-container .productSearchInput {
      height: 38px !important;
      font-size: 14px !important;
      padding: 8px 10px 8px 38px !important;
    }
  }

  /* Touch device optimizations */
  @media (hover: none) {
    .sales-container .productCard:hover {
      transform: none !important;
    }
    .sales-container .addToCartBtn:hover {
      background: #f8f9fa !important;
    }
    .sales-container .qtyBtn:hover {
      background: #F0F0F0 !important;
    }
    .paymentModalStyles .paymentMethodButton:hover {
      transform: none !important;
    }
    .paymentModalStyles .discountButton:hover {
      transform: none !important;
    }
  }

  /* Prevent zoom on input focus for touch devices */
  @media (max-width: 768px) {
    input, select, textarea {
      font-size: 16px !important;
    }
  }
`;
document.head.appendChild(responsiveStyles);